import { useAppDispatch, useAppSelector } from '@/app/reduxHooks'
import { store } from '@/app/store'
import { ResolvedIcon } from '@/features/adp/components/shared/ResolvedIcon'
import { useIntegrationData } from '@/features/adp/hooks/useIntegrationData'
import { syncCanvasState } from '@/features/adp/state/canvas/canvasSlice'
import { sourceService } from '@/features/adp/services/source/source.service'
import { Transformation } from '@/features/adp/services/common/common.types'
import {
  RegisteredSourceFilesResponse,
  SourceFileRegisteredField,
} from '@/features/adp/services/source/source.types'
import { selectCanvasEdges, selectCanvasNodes } from '@/features/adp/state/canvas/canvasSelectors'
import { closeOverlayAsync } from '@/features/adp/state/overlay.thunks'
import { UnionIntegrationSheetOverlayData } from '@/features/adp/state/overlaySlice'
import { Button } from '@/ui/button'
import { Label } from '@/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/ui/select'
import { Separator } from '@/ui/separator'
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/ui/sheet'
import { useQueries, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { getAllUpstreamSourceNodes } from '../../canvas/canvas.utils'

const DUPLICATES_OPTIONS = ['yes', 'no'] as const
type DuplicatesChoice = (typeof DUPLICATES_OPTIONS)[number]

/** Sentinel for the "No sort" option in the SortBy Select. Radix's SelectItem
 *  refuses an empty string value, so we use this token in the UI and convert
 *  back to `null` at the formData boundary. */
const SORT_BY_NONE = '__none__'

/**
 * Persisted per-row Union configuration. One row per secondary input table.
 * Mirrors Angular `prepareJSON()` output in `source-destination/union/union.component.ts`.
 */
export interface UnionRowFormData {
  /** Primary table name at the time this row was saved. First row uses the
   *  real primary tableName; chained rows get `'output'` from the builder. */
  primaryTableName: string
  /** Secondary input table name. */
  withTableName: string
  /** `source_name` for the secondary source (Angular: `WithTable_DataSource`). */
  withTableDataSource: string
  /** `'yes'` for UNION ALL, `'no'` for UNION DISTINCT. */
  withDuplicates: DuplicatesChoice
  /** Optional column name to sort the union output by; `null` when unset. */
  sortBy: string | null
}

interface UpstreamTableSnapshot {
  nodeId: string
  tableName: string
  dataSourceName: string
  columns: Array<{ fieldName: string; dataType: string }>
}

/**
 * Either a fully-resolved upstream source (columns already in node session) or
 * one that needs fetching via `/source-files/registered` because the user
 * hasn't gone through the source's Explorer configuration yet.
 */
interface RawUpstreamEntry {
  nodeId: string
  resolved: UpstreamTableSnapshot | null
  sourceDataSourceId: number | null
  fallbackDataSourceName: string
}

function collectRawUpstreamEntries(
  nodeId: string,
  canvasNodes: ReturnType<typeof useAppSelector>,
  canvasEdges: ReturnType<typeof useAppSelector>,
): RawUpstreamEntry[] {
  const sources = getAllUpstreamSourceNodes(
    nodeId,
    canvasNodes as never,
    canvasEdges as never,
  )

  return sources.map((entry): RawUpstreamEntry => {
    const sourceData = (
      entry.sessionData as
        | { sourceData?: { data?: Record<string, unknown> } }
        | null
        | undefined
    )?.sourceData?.data ?? null

    const tableName =
      typeof sourceData?.source_selected_table === 'string'
        ? sourceData.source_selected_table
        : ''
    const dataSourceName =
      typeof sourceData?.source_name === 'string' ? sourceData.source_name : ''
    const files = Array.isArray(sourceData?.source_files)
      ? (sourceData.source_files as SourceFileRegisteredField[])
      : []
    const columns = files
      .map((field) => ({
        fieldName: typeof field.field_name === 'string' ? field.field_name : '',
        dataType:
          typeof field.field_data_type === 'string'
            ? field.field_data_type.toLowerCase()
            : '',
      }))
      .filter((c) => c.fieldName.length > 0)

    const sourceDataSourceId =
      typeof sourceData?.source_data_source_id === 'number'
        ? sourceData.source_data_source_id
        : null

    if (tableName && columns.length > 0) {
      return {
        nodeId: entry.nodeId,
        sourceDataSourceId,
        fallbackDataSourceName: dataSourceName,
        resolved: { nodeId: entry.nodeId, tableName, dataSourceName, columns },
      }
    }

    return {
      nodeId: entry.nodeId,
      sourceDataSourceId,
      fallbackDataSourceName: dataSourceName,
      resolved: null,
    }
  })
}

function snapshotFromRegisteredTables(
  nodeId: string,
  dataSourceName: string,
  registered: RegisteredSourceFilesResponse,
): UpstreamTableSnapshot | null {
  const first = registered[0]
  if (!first) return null
  const columns = first.fields
    .map((field) => ({
      fieldName: typeof field.field_name === 'string' ? field.field_name : '',
      dataType:
        typeof field.data_type === 'string' ? field.data_type.toLowerCase() : '',
    }))
    .filter((c) => c.fieldName.length > 0)
  return {
    nodeId,
    tableName: first.table_name,
    dataSourceName,
    columns,
  }
}

interface UnionRowDraft {
  withDuplicates: DuplicatesChoice
  sortBy: string | null
}

/**
 * Compare primary vs secondary column lists for Union compatibility. Union
 * semantically requires the same schema across all inputs — both column
 * names AND data types must match (case-insensitive name match; trimmed
 * lowercased type comparison). Returns a human-readable mismatch summary,
 * or `null` if the secondary is compatible.
 */
function describeUnionSchemaMismatch(
  primary: UpstreamTableSnapshot,
  secondary: UpstreamTableSnapshot,
): string | null {
  const norm = (s: string) => s.trim().toLowerCase()
  const primaryByName = new Map<string, string>()
  for (const c of primary.columns) primaryByName.set(norm(c.fieldName), norm(c.dataType))
  const secondaryByName = new Map<string, string>()
  for (const c of secondary.columns) secondaryByName.set(norm(c.fieldName), norm(c.dataType))

  if (primaryByName.size !== secondaryByName.size) {
    return `column count differs (${primary.tableName} has ${primaryByName.size}, ${secondary.tableName} has ${secondaryByName.size})`
  }

  const missingInSec: string[] = []
  const typeMismatch: string[] = []
  for (const [name, primaryType] of primaryByName) {
    if (!secondaryByName.has(name)) {
      missingInSec.push(name)
      continue
    }
    const secType = secondaryByName.get(name) ?? ''
    if (secType && primaryType && secType !== primaryType) {
      typeMismatch.push(`${name} (${primaryType} vs ${secType})`)
    }
  }
  if (missingInSec.length > 0) {
    return `${secondary.tableName} is missing column(s): ${missingInSec.slice(0, 4).join(', ')}${missingInSec.length > 4 ? '…' : ''}`
  }
  if (typeMismatch.length > 0) {
    return `data-type mismatch on ${typeMismatch.slice(0, 3).join('; ')}${typeMismatch.length > 3 ? '…' : ''}`
  }
  return null
}

const UnionIntegrationSheet = () => {
  const dispatch = useAppDispatch()
  const queryClient = useQueryClient()
  const { isOpen, type, data } = useAppSelector((state) => state.overlay)
  const isVisible = isOpen && type === 'union-integration-sheet'
  const overlayData = isVisible ? (data as UnionIntegrationSheetOverlayData | null) : null

  const canvasNodes = useAppSelector(selectCanvasNodes)
  const canvasEdges = useAppSelector(selectCanvasEdges)
  const { setIntegrationData, getIntegrationDataByNodeId, clearIntegrationData } =
    useIntegrationData()

  const catalog: Transformation[] | undefined = queryClient.getQueryData(['sidebar-data'])?.[2]
  const integrationMeta = useMemo(
    () =>
      catalog?.find((t) => t?.id === Number(overlayData?.transformationId)) ?? null,
    [catalog, overlayData?.transformationId],
  )

  const rawUpstream = useMemo<RawUpstreamEntry[]>(() => {
    if (!overlayData?.nodeId) return []
    return collectRawUpstreamEntries(overlayData.nodeId, canvasNodes, canvasEdges)
  }, [overlayData?.nodeId, canvasNodes, canvasEdges])

  // Fetch /source-files/registered for any unconfigured upstream source so the
  // user can review union secondaries without first opening each source's Explorer.
  const fetchQueries = useQueries({
    queries: rawUpstream.map((entry) => {
      const id = entry.sourceDataSourceId ?? 0
      const enabled = isVisible && entry.resolved === null && id > 0
      return {
        queryKey: ['registered-source-files', id],
        queryFn: () => sourceService.listRegisteredSourceFiles(id),
        enabled,
        staleTime: Infinity,
      }
    }),
  })

  const upstreamFingerprint = rawUpstream
    .map((entry, index) =>
      entry.resolved
        ? `r:${entry.nodeId}:${entry.resolved.tableName}:${entry.resolved.columns.length}`
        : `f:${entry.nodeId}:${fetchQueries[index]?.dataUpdatedAt ?? 0}`,
    )
    .join('|')

  const upstreamTables = useMemo<UpstreamTableSnapshot[]>(() => {
    return rawUpstream
      .map((entry, index): UpstreamTableSnapshot | null => {
        if (entry.resolved) return entry.resolved
        const data = fetchQueries[index]?.data
        if (!data) return null
        return snapshotFromRegisteredTables(
          entry.nodeId,
          entry.fallbackDataSourceName,
          data,
        )
      })
      .filter((s): s is UpstreamTableSnapshot => s !== null)
  }, [rawUpstream, upstreamFingerprint])

  const primary = upstreamTables[0] ?? null
  const secondaries = useMemo(() => upstreamTables.slice(1), [upstreamTables])

  const [rows, setRows] = useState<UnionRowDraft[]>([])

  // Rehydrate from session when sheet opens or upstream count changes.
  useEffect(() => {
    if (!isVisible || !overlayData?.nodeId) return

    const stored = getIntegrationDataByNodeId(overlayData.nodeId)
    const storedItems =
      stored?.data &&
      Array.isArray((stored.data as { data?: unknown }).data)
        ? (stored.data as { data: Array<{ formData?: UnionRowFormData }> }).data
        : []

    const draftsFromStorage = storedItems.map((item) => {
      const rawSortBy = item.formData?.sortBy
      // Coalesce '' → null so the Select value never lands on an empty string.
      const sortBy = typeof rawSortBy === 'string' && rawSortBy.trim().length > 0
        ? rawSortBy
        : null
      return {
        withDuplicates: (item.formData?.withDuplicates ?? 'yes') as DuplicatesChoice,
        sortBy,
      }
    })

    setRows(
      secondaries.map((_, index) =>
        draftsFromStorage[index] ?? {
          withDuplicates: 'yes' as DuplicatesChoice,
          sortBy: null,
        },
      ),
    )
  }, [isVisible, overlayData?.nodeId, secondaries, getIntegrationDataByNodeId])

  const updateRow = (index: number, patch: Partial<UnionRowDraft>) => {
    setRows((current) =>
      current.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    )
  }

  const handleClose = () => dispatch(closeOverlayAsync())
  const handleOpenChange = (open: boolean) => {
    if (!open) handleClose()
  }

  const handleReset = () => {
    if (!overlayData?.nodeId) return
    clearIntegrationData(overlayData.nodeId)
    setRows(
      secondaries.map(() => ({
        withDuplicates: 'yes' as DuplicatesChoice,
        sortBy: null,
      })),
    )
    toast.success('Union configuration reset')
  }

  const handleSave = () => {
    if (!overlayData?.nodeId || !primary || secondaries.length === 0) return

    // Union semantically requires matching schemas (column names + types)
    // across all inputs — backend will reject incompatible unions. Catch
    // mismatches client-side so the user gets a precise hint instead of an
    // opaque backend error after Save.
    for (const sec of secondaries) {
      const mismatch = describeUnionSchemaMismatch(primary, sec)
      if (mismatch) {
        toast.error(
          `Union requires matching schemas: ${mismatch}. Align the column lists on both sources, then try again.`,
        )
        return
      }
    }

    // Build items. Chain: subsequent rows operate on the merged "output" virtual
    // table. Builder (`buildUnionEntries`) substitutes the real SourceTable for
    // the first emitted row and 'output' for the rest via state.totalEmitted,
    // so primaryTableName here is informational; we still capture it for parity
    // with Join's saved shape and for debugging session dumps.
    const items: Array<{ id: string; label: string; formData: UnionRowFormData }> = []
    let runningPrimaryTableName = primary.tableName

    for (let i = 0; i < rows.length; i++) {
      const sec = secondaries[i]
      const row = rows[i]
      items.push({
        id: crypto.randomUUID(),
        label: `Union ${i + 1}`,
        formData: {
          primaryTableName: runningPrimaryTableName,
          withTableName: sec.tableName,
          withTableDataSource: sec.dataSourceName,
          withDuplicates: row.withDuplicates,
          sortBy: row.sortBy && row.sortBy.trim().length > 0 ? row.sortBy : null,
        },
      })
      runningPrimaryTableName = 'output'
    }

    if (!integrationMeta) {
      toast.error('Catalog entry for Union is missing. Reopen the sheet and try again.')
      return
    }

    setIntegrationData(overlayData.nodeId, {
      id: integrationMeta.id,
      name: integrationMeta.name,
      description: integrationMeta.description,
      abbreviation: integrationMeta.abbreviation,
      data: items,
    })

    // Match Join's pattern of writing a short summary to the canvas card.
    const secondaryTitle = secondaries.map((s) => s.tableName).filter(Boolean).join(', ')
    if (secondaryTitle) {
      const snapshot = store.getState()
      const nextNodes = snapshot.canvas.present.nodes.map((node) => {
        if (node.id !== overlayData.nodeId || node.type !== 'integration') return node
        return {
          ...node,
          data: {
            ...node.data,
            nodeSecondaryTitle: secondaryTitle,
          },
        } as typeof node
      })
      dispatch(
        syncCanvasState({
          nodes: nextNodes,
          edges: snapshot.canvas.present.edges,
        }),
      )
    }

    toast.success('Union configuration saved')
    dispatch(closeOverlayAsync())
  }

  return (
    <Sheet open={isVisible} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        noBackdrop
        topOffset={64}
        className="shadow-none w-full sm:max-w-[640px] flex flex-col"
      >
        <SheetHeader>
          <SheetTitle>Union Details</SheetTitle>
        </SheetHeader>

        <p className="text-sm px-5 text-muted-foreground pb-2">
          Stack rows from multiple upstream sources with compatible schemas.
        </p>
        <Separator />

        <div className="flex items-center gap-3 px-5 py-3">
          <div className="rounded-md border shadow-sm p-2">
            <ResolvedIcon
              imageLink={integrationMeta?.image_link}
              alt={integrationMeta?.name}
              className="h-12 w-12 object-contain"
            />
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="font-medium text-sm">{integrationMeta?.name ?? 'Union'}</span>
            <span className="text-xs text-muted-foreground">
              {integrationMeta?.description ?? 'Stack rows from compatible sources'}
            </span>
          </div>
        </div>

        <Separator />

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {!primary && (
            <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
              Connect a Source upstream to start configuring the union.
            </div>
          )}

          {primary && secondaries.length === 0 && fetchQueries.some((q) => q.isPending) && (
            <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
              Loading source columns…
            </div>
          )}

          {primary &&
            secondaries.length === 0 &&
            !fetchQueries.some((q) => q.isPending) && (
              <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                Connect a second Source upstream to enable union configuration.
              </div>
            )}

          {primary && secondaries.length > 0 && (
            <>
              <div className="rounded-md border p-4 bg-muted/30">
                <Label className="text-xs uppercase text-muted-foreground tracking-wide">
                  Primary Table
                </Label>
                <p className="mt-1 text-sm font-medium">{primary.tableName}</p>
                <p className="text-xs text-muted-foreground">{primary.dataSourceName}</p>
              </div>

              {secondaries.map((sec, index) => {
                const row = rows[index]
                if (!row) return null
                const schemaMismatch = describeUnionSchemaMismatch(primary, sec)

                return (
                  <div
                    key={sec.nodeId}
                    className="rounded-md border p-4 space-y-3"
                  >
                    <div>
                      <Label className="text-xs uppercase text-muted-foreground tracking-wide">
                        Union {index + 1} — With Table
                      </Label>
                      <p className="mt-1 text-sm font-medium">{sec.tableName}</p>
                      <p className="text-xs text-muted-foreground">
                        {sec.dataSourceName}
                      </p>
                    </div>
                    {schemaMismatch && (
                      <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                        Schema mismatch: {schemaMismatch}. Union requires the
                        same column names and types across all inputs.
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1">
                        <Label className="text-xs">Include Duplicates</Label>
                        <Select
                          value={row.withDuplicates}
                          onValueChange={(value) =>
                            updateRow(index, {
                              withDuplicates: value as DuplicatesChoice,
                            })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {DUPLICATES_OPTIONS.map((choice) => (
                              <SelectItem key={choice} value={choice}>
                                {choice === 'yes' ? 'Yes (UNION ALL)' : 'No (UNION DISTINCT)'}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs">Sort By (optional)</Label>
                        <Select
                          // Radix throws on `SelectItem value=""`, so use a
                          // sentinel for the "No sort" option and convert
                          // both ways at the boundary.
                          value={row.sortBy ?? SORT_BY_NONE}
                          onValueChange={(value) =>
                            updateRow(index, {
                              sortBy: value === SORT_BY_NONE ? null : value,
                            })
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="No sort" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={SORT_BY_NONE}>No sort</SelectItem>
                            {primary.columns
                              // Defensive: Radix throws on any SelectItem with
                              // value="". The upstream collectors already
                              // filter empty fieldName but keep this here so a
                              // dirty source can't crash the sheet on open.
                              .filter((col) => col.fieldName.trim().length > 0)
                              .map((col) => (
                                <SelectItem key={col.fieldName} value={col.fieldName}>
                                  {col.fieldName}
                                </SelectItem>
                              ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                )
              })}
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-border bg-background">
          <Button type="button" variant="outline" onClick={handleReset}>
            Reset
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={!primary || secondaries.length === 0}
          >
            Save
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  )
}

export default UnionIntegrationSheet
